import { ensureFresh, forceRefresh, isActive } from '../services/tokenManager';

// src/api/ApiClient.ts

/** Human message from an error this client throws ("… - <jsonBody>"): the
 *  server envelope's `message` when present, else the raw tail. */
export function apiErrorMessage(
  error: unknown,
  fallback = 'Something went wrong.',
): string {
  if (!(error instanceof Error)) return fallback;
  const raw = error.message;
  const sep = raw.indexOf(' - ');
  const body = sep >= 0 ? raw.slice(sep + 3) : raw;
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
  } catch {
    // body isn't JSON — fall through to the raw tail
  }
  return body || fallback;
}

export class ApiClient {
  private token: string;
  private baseUrl: string;

  constructor(token: string, baseUrl: string) {
    this.token = token;
    this.baseUrl = baseUrl;
  }

  public updateToken(token: string) {
    this.token = token;
  }

  /** Authenticated fetch: renews a near-expiry token first, retries once on
   *  401 (safe for POST/PUT — the authorizer rejects before the Lambda runs).
   *  Manager inactive (localhost / legacy boot) → plain fetch, as before. */
  private async fetchWithAuth(
    fullUrl: string,
    init: {
      method: 'GET' | 'POST' | 'PUT' | 'DELETE';
      body?: BodyInit;
      baseHeaders?: HeadersInit;
    },
  ): Promise<Response> {
    if (isActive()) {
      try {
        this.token = await ensureFresh();
      } catch {
        // fire with the held token; the dead flag drives the UI fallback
      }
    }
    const doFetch = (): Promise<Response> => {
      const headers = new Headers(init.baseHeaders);
      headers.set('X-WBAPI-JWT', this.token);
      return fetch(fullUrl, {
        method: init.method,
        headers,
        ...(init.body !== undefined ? { body: init.body } : {}),
      });
    };
    let response = await doFetch();
    if (response.status === 401 && isActive()) {
      // server rejected a token ensureFresh considered valid — force-refresh
      try {
        this.token = await forceRefresh();
      } catch {
        return response;
      }
      response = await doFetch();
    }
    return response;
  }

  // Flexible GET Request Method with full URL
  async get<T>(subUrl: string, body?: any, headers?: HeadersInit): Promise<T> {
    return this.request<T>(this.baseUrl + subUrl, 'GET', body, headers);
  }

  // POST Request Method with full URL
  async post<T>(subUrl: string, body?: any, headers?: HeadersInit, customEndpoint?: string): Promise<T> {
    // Determine the endpoint to use: customEndpoint (if provided) or the default baseUrl
    const fullUrl = (customEndpoint || this.baseUrl) + subUrl;

    // Pass the determined full URL to the general request method
    return this.request<T>(fullUrl, 'POST', body, headers);
  }

  // PUT Request Method with full URL
  async put<T>(subUrl: string, body?: any, headers?: HeadersInit): Promise<T> {
    return this.request<T>(this.baseUrl + subUrl, 'PUT', body, headers);
  }

  // DELETE Request Method with full URL
  async delete<T>(subUrl: string, body?: any, headers?: HeadersInit): Promise<T> {
    return this.request<T>(this.baseUrl + subUrl, 'DELETE', body, headers);
  }

  async putFile<T>(fullUrl: string, file: Blob, contentType?: string): Promise<boolean> {
    const headers = new Headers();
    if (contentType) {
      headers.append('Content-Type', contentType);
    }

    const options: RequestInit = {
      method: 'PUT',
      body: file,
      headers: headers
    };
    const response = await fetch(fullUrl, options);
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return await response.ok;
  }

  async getS3Content<T>(subUrl: string): Promise<T> {
    try {
      // Step 1: Construct the full API URL
      const fullUrl = `${this.baseUrl}${subUrl}`;

      // Step 2: Make the API request (authenticated; auto-renews the JWT)
      const apiResponse = await this.fetchWithAuth(fullUrl, { method: 'GET' });

      // Step 3: Handle response status
      if (!apiResponse.ok) {
        const errorBody = await apiResponse.text().catch(() => '<could not read body>');
        throw new Error(
          `API call failed: ${apiResponse.status} ${apiResponse.statusText} - ${errorBody}`
        );
      }

      // Step 4: Parse and return JSON response
      const jsonResponse = await apiResponse.json(); // Parse JSON from response
      return jsonResponse as T; // Assert the response matches the expected type
    } catch (error) {
      // Safely narrow the error type
      if (error instanceof Error) {
        throw new Error(`Failed to retrieve content from S3: ${error.message}`);
      } else {
        throw new Error('Failed to retrieve content from S3: An unknown error occurred.');
      }
    }
  }

  /**
   * Like getS3Content but returns the raw text body alongside its byte length.
   * Used by the perf-baseline instrumentation to time download separately from
   * JSON.parse. Caller is responsible for parsing.
   */
  async getS3Text(subUrl: string): Promise<{ text: string; bytes: number }> {
    const fullUrl = `${this.baseUrl}${subUrl}`;
    const apiResponse = await this.fetchWithAuth(fullUrl, { method: 'GET' });
    if (!apiResponse.ok) {
      const errorBody = await apiResponse.text().catch(() => '<could not read body>');
      throw new Error(
        `API call failed: ${apiResponse.status} ${apiResponse.statusText} - ${errorBody}`
      );
    }
    const text = await apiResponse.text();
    return { text, bytes: text.length };
  }

  /**
   * Same contract as getS3Content but returns the raw bytes instead of parsing
   * as JSON. The upstream Lambda returns a 302 redirect to a presigned S3 URL;
   * fetch() follows the redirect, so `arrayBuffer()` gives us the S3 object's
   * raw content — parquet, binary, whatever the bucket holds.
   */
  async getS3ArrayBuffer(subUrl: string): Promise<ArrayBuffer> {
    const fullUrl = `${this.baseUrl}${subUrl}`;
    const apiResponse = await this.fetchWithAuth(fullUrl, { method: 'GET' });
    if (!apiResponse.ok) {
      const errorBody = await apiResponse.text().catch(() => '<could not read body>');
      throw new Error(
        `API call failed: ${apiResponse.status} ${apiResponse.statusText} - ${errorBody}`
      );
    }
    return await apiResponse.arrayBuffer();
  }


  // General request method
  private async request<T>(
    fullUrl: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: any,
    headersInit?: HeadersInit
  ): Promise<T> {
    // Initialize headers (JWT attached inside fetchWithAuth)
    const headers = new Headers(headersInit);
    headers.append('Content-Type', 'application/json');

    // Perform the authenticated fetch (auto-renews the JWT)
    const response = await this.fetchWithAuth(fullUrl, {
      method,
      baseHeaders: headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    // Handle non-OK responses
    if (!response.ok) {
      const errorText = await response.text().catch(() => null);
      throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Parse and return JSON response
    return await response.json();
  }
}
